export interface RestrictedTradingCategory {
    state: string;
    categories: string[];
    subcategories?: string[];
    /** Marks entries enforced through GeoComply location verification. */
    geo?: boolean;
    /**
     * Lets position-capped sells through in this state, so positions already
     * held in its restricted categories can still be closed.
     */
    sell_positions_allowed?: boolean;
}

export interface FeatureFlags {
    blockNyZeroHash: boolean;
    isCardPaymentEnabled: boolean;
    interestRate: string;
    interestRateAmount?: string;
    /** The perps account's rate; the store falls back to `interestRateAmount` when S3 leaves it empty. */
    marginInterestRateAmount?: string;
    enableWithdrawalMfa: boolean;
    enableNewMfaMethods: boolean;
    cryptoWithdrawEnabled: boolean;
    cryptoDepositEnabled: boolean;
    globalParlaysEnabled?: boolean;
    /** Always populated — seeded to `[]` in `defaultFeatureFlags` and by the `|| []` hydration path. */
    marketsToRemove: string[];
    attributionSurveyTitles?: string[];
    enableProps?: boolean;
    braintreePaypalEnabled?: boolean;
    braintreeVenmoEnabled?: boolean;
    bracketRules?: string;
    bracketEspnImportEnabled?: boolean;
    restrictedTradingCategories?: RestrictedTradingCategory[];
    worldCupPromoBanner?: boolean;
    /**
     * Kill switch for pausing the event page's covered content (live subscriptions + CSS
     * animations) while the mobile ticket is open on a low-memory device. Defaults ON; set the S3
     * field to `false` to disable it without a deploy. It changes WS subscription lifecycle under
     * live price UI, so it needs a lever faster than a revert.
     */
    coveredContentPauseEnabled?: boolean;
    /**
     * The CFB Undefeated Tracker's `cfb_undefeated` block, stored raw. Validation
     * lives in `lib/cfbUndefeated/config.ts` (`resolveCfbUndefeatedConfig`) so a bad
     * S3 edit can never throw inside the store.
     */
    cfbUndefeated?: unknown;
    /**
     * Protected props: `prop_to_protection` (base series → protected series) and
     * `protection_messages` (series → what protection means), stored raw.
     * Validated by `lib/protectedProps/protectedProps.ts`.
     */
    propToProtection?: unknown;
    protectionMessages?: unknown;
}

export const defaultFeatureFlags: FeatureFlags = {
    blockNyZeroHash: true,
    isCardPaymentEnabled: true,
    interestRate: '',
    interestRateAmount: '',
    marginInterestRateAmount: '',
    enableWithdrawalMfa: false,
    enableNewMfaMethods: false,
    cryptoWithdrawEnabled: false,
    cryptoDepositEnabled: false,
    globalParlaysEnabled: false,
    marketsToRemove: [],
    attributionSurveyTitles: [],
    enableProps: false,
    braintreePaypalEnabled: true,
    braintreeVenmoEnabled: true,
    bracketEspnImportEnabled: false,
    restrictedTradingCategories: [],
    worldCupPromoBanner: false,
    coveredContentPauseEnabled: true,
};

export interface FeatureFlagsReturn {
    block_new_yorkers_zero_hash: boolean;
    is_card_payment_enabled: boolean;
    interest_rate: string;
    interest_rate_amount: string;
    margin_interest_rate_amount?: string;
    enable_withdrawal_mfa: boolean;
    enable_new_mfa_methods: boolean;
    crypto_withdraw_enabled: boolean;
    crypto_deposit_enabled: boolean;
    global_parlays_enabled: boolean;
    markets_to_remove: string[];
    attribution_survey_titles: string[];
    enable_props: boolean;
    braintree_paypal_enabled: boolean;
    braintree_venmo_enabled: boolean;
    bracket_rules?: string;
    bracket_espn_import_enabled?: boolean;
    restricted_trading_categories?: RestrictedTradingCategory[];
    world_cup_promo_banner?: boolean;
    covered_content_pause_enabled?: boolean;
    cfb_undefeated?: unknown;
    prop_to_protection?: unknown;
    protection_messages?: unknown;
    /** Curated Home "Upcoming" rail; parsed server-side in `lib/server/importantEventsServer.ts`. */
    important_events_calendar_events?: unknown;
}
